import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Container, TextField, Button,
  CircularProgress, Divider, Dialog, DialogContent, IconButton,
  Popover, Snackbar, Alert
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import StarIcon from '@mui/icons-material/Star';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GroupsIcon from '@mui/icons-material/Groups';
import CloseIcon from '@mui/icons-material/Close';
import RouteIcon from '@mui/icons-material/Route';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SecurityIcon from '@mui/icons-material/Security';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import VerifiedIcon from '@mui/icons-material/Verified';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { pushToGoogleSheet, setSheetHeaderRow } from '../utils/googleSheets';

const PURPLE = '#5B2D8E';
const PURPLE_LT = '#7B4BB3';
const PURPLE_DK = '#4A2474';
const PURPLE_BG = '#F3F0F7';
const TEXT_DARK = '#1A1A2E';
const TEXT_MID = '#666677';
const TEXT_LIGHT = '#999aaa';

const HERO_IMG = '/tirumala-hero.png';
const TEMPLE_IMG = '/tirumala-temple.png';

// ── Google Sheet headers for BangaloreToTirumala sheet ──────────────────────
// Column headers — written once at A1 via setSheetHeaderRow (PUT, not append)
const TIRUMALA_HEADERS = [
  'Trip Type',
  'Travel Date',
  'Pickup City',
  'Drop City',
  'Passenger Name',
  'Phone Number',
];

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px', backgroundColor: '#F5F5F8', fontSize: '0.88rem', fontFamily: "'Poppins',sans-serif",
    '& fieldset': { borderColor: 'transparent' },
    '&:hover fieldset': { borderColor: PURPLE },
    '&.Mui-focused fieldset': { borderColor: PURPLE, borderWidth: 1.5 },
  },
  '& .MuiInputBase-input': { color: TEXT_DARK, py: 1.3, px: 1.5 },
  '& .MuiInputBase-input::placeholder': { color: '#aaa', opacity: 1 },
  '& .MuiFormHelperText-root': { color: '#d32f2f', fontSize: '0.7rem', ml: 0.5, mt: 0.2 },
};

// ── Shared calendar date-picker (same look as BookingForm.js) ────────────────
const CalendarPicker = ({ label, selectedDate, onSelect }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const todayDate = (() => { const d = new Date(); d.setHours(0,0,0,0); return d; })();
  const [calMonth, setCalMonth] = useState(selectedDate || todayDate);

  const fmt = (date) => {
    if (!date) return 'Select Date';
    const d = new Date(date); d.setHours(0,0,0,0);
    const diff = Math.round((d - todayDate) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const prevMonth = () => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() - 1, 1));
  const nextMonth = () => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 1));

  const daysInMonth = new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 0).getDate();
  const firstDow = new Date(calMonth.getFullYear(), calMonth.getMonth(), 1).getDay();
  const offset = firstDow === 0 ? 6 : firstDow - 1;

  return (
    <>
      <Box
        onClick={(e) => { setCalMonth(selectedDate || todayDate); setAnchorEl(e.currentTarget); }}
        sx={{
          border: '1.5px solid #E8E0F0', borderRadius: '10px', p: '10px 14px',
          display: 'flex', alignItems: 'center', gap: 1.2, cursor: 'pointer',
          bgcolor: '#F5F5F8', '&:hover': { borderColor: PURPLE }, transition: 'border-color 0.2s'
        }}
      >
        <CalendarTodayIcon sx={{ fontSize: 16, color: PURPLE }} />
        <Typography sx={{ fontSize: '0.88rem', color: selectedDate ? TEXT_DARK : '#aaa', fontWeight: 500, fontFamily: "'Poppins',sans-serif" }}>
          {fmt(selectedDate)}
        </Typography>
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 3, borderRadius: '16px', width: 290, mt: 1, boxShadow: '0 12px 40px rgba(0,0,0,0.14)' } } }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, fontFamily: "'Poppins',sans-serif", color: '#333' }}>
            {label || 'Select Date'}
          </Typography>
          <Button size="small" onClick={() => { onSelect(new Date(todayDate)); setAnchorEl(null); }}
            sx={{ textTransform: 'none', fontSize: '0.7rem', fontWeight: 600, color: PURPLE, p: 0, '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}>
            Today
          </Button>
        </Box>

        {/* Month navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 0.5 }}>
          <IconButton size="small" onClick={prevMonth}
            disabled={calMonth.getFullYear() === todayDate.getFullYear() && calMonth.getMonth() === todayDate.getMonth()}
            sx={{ color: PURPLE, bgcolor: '#f5f0fa', '&:hover': { bgcolor: '#ece4f5' }, '&:disabled': { opacity: 0.3 } }}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, fontFamily: "'Poppins',sans-serif", color: '#333' }}>
            {calMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </Typography>
          <IconButton size="small" onClick={nextMonth} sx={{ color: PURPLE, bgcolor: '#f5f0fa', '&:hover': { bgcolor: '#ece4f5' } }}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Day grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, textAlign: 'center', mb: 1 }}>
          {['Mo','Tu','We','Th','Fr','Sa','Su'].map(d => (
            <Typography key={d} sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#666' }}>{d}</Typography>
          ))}
          {[...Array(offset)].map((_, i) => <Box key={`e${i}`} />)}
          {[...Array(daysInMonth)].map((_, i) => {
            const day = new Date(calMonth.getFullYear(), calMonth.getMonth(), i + 1);
            day.setHours(0,0,0,0);
            const isPast = day < todayDate;
            const isSel = selectedDate && selectedDate.getTime() === day.getTime();
            return (
              <Box key={i} onClick={() => { if (!isPast) { onSelect(new Date(day)); setAnchorEl(null); } }}
                sx={{
                  fontSize: '0.75rem', p: 0.8, borderRadius: '6px',
                  cursor: isPast ? 'not-allowed' : 'pointer', opacity: isPast ? 0.3 : 1,
                  bgcolor: isSel ? PURPLE : 'transparent', color: isSel ? '#fff' : '#333',
                  fontWeight: isSel ? 600 : 400,
                  '&:hover': { bgcolor: isPast ? 'transparent' : (isSel ? PURPLE : '#f0f0f0') }
                }}>
                {i + 1}
              </Box>
            );
          })}
        </Box>
      </Popover>
    </>
  );
};

// ── Booking Card ─────────────────────────────────────────────────────────────
const BookingCard = () => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [travelDate, setTravelDate] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '' });

  // Set attractive headers at row A1 once when page loads (PUT = idempotent, never duplicates)
  useEffect(() => {
    setSheetHeaderRow('BangaloreToTirumala', TIRUMALA_HEADERS);
  }, []);

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = 'Name is required';
    if (!/^[6-9]\d{9}$/.test(phone)) e.phone = 'Enter valid 10-digit number';
    if (!travelDate) e.date = 'Please select travel date';
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({}); setIsSubmitting(true);
    try {
      const dateStr = travelDate.toLocaleDateString('en-GB');
      await pushToGoogleSheet('BangaloreToTirumala', [
        'Round Trip', dateStr, 'Bangalore, Karnataka', 'Tirumala, Andhra Pradesh', name, '+91' + phone
      ]);
      setShowSuccess(true);
      setName(''); setPhone(''); setTravelDate(null);
    } catch {
      setSnack({ open: true, message: 'Booking failed. Please call +91 99648 48414' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Box sx={{ backgroundColor: '#ffffff', borderRadius: '24px', boxShadow: '0 20px 60px rgba(0,0,0,0.1), 0 8px 18px rgba(91,45,142,0.07)', border: '1px solid rgba(91,45,142,0.08)', overflow: 'hidden' }}>

        {/* ── Purple gradient header ── */}
        <Box sx={{ background: `linear-gradient(135deg, ${PURPLE_DK} 0%, ${PURPLE} 55%, ${PURPLE_LT} 100%)`, px: { xs: 2.5, md: 3 }, pt: 2.8, pb: 2.5 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.7, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: '50px', px: 1.5, py: 0.4, mb: 1.2 }}>
            <VerifiedIcon sx={{ fontSize: 13, color: '#fff' }} />
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.65rem', letterSpacing: '1px', fontFamily: "'Poppins',sans-serif" }}>ROUND TRIP FREE INCLUDED</Typography>
          </Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color: '#fff', fontFamily: "'Poppins',sans-serif", lineHeight: 1.2, mb: 0.4 }}>Book Your Cab Now</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.75rem', mb: 2, fontFamily: "'Poppins',sans-serif" }}>Instant confirmation · No advance payment</Typography>

          {/* Route strip inside header */}
          <Box sx={{ bgcolor: 'rgba(255,255,255,0.12)', borderRadius: '12px', p: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.8 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4ade80', flexShrink: 0 }} />
              <Typography sx={{ color: '#fff', fontSize: '0.82rem', fontWeight: 600, fontFamily: "'Poppins',sans-serif" }}>Bangalore, Karnataka</Typography>
            </Box>
            <Box sx={{ ml: '3.5px', height: 16, borderLeft: '1.5px dashed rgba(255,255,255,0.35)' }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <LocationOnIcon sx={{ fontSize: 14, color: '#fbbf24' }} />
              <Typography sx={{ color: '#fff', fontSize: '0.82rem', fontWeight: 600, fontFamily: "'Poppins',sans-serif" }}>Tirumala, Andhra Pradesh</Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ p: { xs: 2.5, md: 3 } }}>

          {/* Full Name */}
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ color: TEXT_MID, fontSize: '0.68rem', fontWeight: 600, mb: 0.5, fontFamily: "'Poppins',sans-serif", letterSpacing: '0.5px' }}>FULL NAME *</Typography>
            <TextField fullWidth size="small" placeholder="Your full name" value={name} onChange={e => setName(e.target.value)} error={!!errors.name} helperText={errors.name} sx={fieldSx} />
          </Box>

          {/* Phone */}
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ color: TEXT_MID, fontSize: '0.68rem', fontWeight: 600, mb: 0.5, fontFamily: "'Poppins',sans-serif", letterSpacing: '0.5px' }}>PHONE NUMBER *</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box sx={{ border: '1px solid #e8e0f0', borderRadius: '10px', display: 'flex', alignItems: 'center', px: 1.2, bgcolor: '#F5F5F8', flexShrink: 0 }}>
                <Typography sx={{ color: TEXT_DARK, fontSize: '0.85rem', fontWeight: 700, fontFamily: "'Poppins',sans-serif" }}>+91</Typography>
              </Box>
              <TextField fullWidth size="small" placeholder="10-digit number" value={phone}
                onChange={e => { const v = e.target.value.replace(/\D/g, ''); if (v.length <= 10) setPhone(v); }}
                error={!!errors.phone} helperText={errors.phone} sx={fieldSx} />
            </Box>
          </Box>

          {/* Travel Date — calendar picker */}
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ color: TEXT_MID, fontSize: '0.68rem', fontWeight: 600, mb: 0.5, fontFamily: "'Poppins',sans-serif", letterSpacing: '0.5px' }}>TRAVEL DATE *</Typography>
            <CalendarPicker label="Travel Date" selectedDate={travelDate} onSelect={setTravelDate} />
            {errors.date && <Typography sx={{ color: '#d32f2f', fontSize: '0.7rem', mt: 0.3, ml: 0.5, fontFamily: "'Poppins',sans-serif" }}>{errors.date}</Typography>}
          </Box>

          <Button fullWidth variant="contained" onClick={handleSubmit} disabled={isSubmitting} sx={{
            py: 1.6, borderRadius: '12px', textTransform: 'none', fontWeight: 700, fontSize: '0.95rem', fontFamily: "'Poppins',sans-serif",
            backgroundColor: PURPLE, color: '#fff', boxShadow: '0 4px 14px rgba(91,45,142,0.35)',
            '&:hover': { backgroundColor: PURPLE_DK, transform: 'translateY(-1px)' },
            transition: 'all 0.25s ease',
            '&.Mui-disabled': { background: '#e8e0f0', color: '#bbb' },
          }}>
            {isSubmitting ? <CircularProgress size={22} sx={{ color: '#fff' }} />
              : <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>Book Divine Journey Now <ArrowForwardIcon sx={{ fontSize: 18 }} /></Box>}
          </Button>
          <Typography sx={{ color: TEXT_LIGHT, fontSize: '0.65rem', textAlign: 'center', mt: 1.2, fontFamily: "'Poppins',sans-serif" }}>
            Free cancellation · Zero hidden charges · GST inclusive
          </Typography>
        </Box>
      </Box>

      {/* Success Dialog */}
      <Dialog open={showSuccess} onClose={() => setShowSuccess(false)} PaperProps={{ sx: { borderRadius: '24px', p: 1, maxWidth: 380 } }}>
        <DialogContent sx={{ textAlign: 'center', px: 4, py: 4 }}>
          <IconButton onClick={() => setShowSuccess(false)} sx={{ position: 'absolute', top: 10, right: 10 }}><CloseIcon /></IconButton>
          <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 44, color: '#22c55e' }} />
          </Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', fontFamily: "'Poppins',sans-serif", color: TEXT_DARK, mb: 1 }}>Booking Received!</Typography>
          <Typography sx={{ color: TEXT_MID, fontFamily: "'Poppins',sans-serif", fontSize: '0.88rem', mb: 2.5 }}>
            Our team will call you within <strong>30 minutes</strong> to confirm your Tirumala cab.
          </Typography>
          <Box component="a" href="tel:+919964848414" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8, textDecoration: 'none', bgcolor: PURPLE, color: '#fff', borderRadius: '50px', px: 3, py: 1.2, fontWeight: 700, fontFamily: "'Poppins',sans-serif", fontSize: '0.9rem', '&:hover': { bgcolor: PURPLE_DK } }}>
            <PhoneIcon sx={{ fontSize: 18 }} /> +91 99648 48414
          </Box>
        </DialogContent>
      </Dialog>

      {/* Error Snackbar — replaces alert() */}
      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={() => setSnack({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack({ open: false, message: '' })}
          severity="error"
          variant="filled"
          sx={{
            borderRadius: '14px', fontFamily: "'Poppins',sans-serif", fontSize: '0.85rem',
            boxShadow: '0 8px 28px rgba(0,0,0,0.18)', fontWeight: 600,
            '& .MuiAlert-icon': { fontSize: 22 },
          }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
};

const PricingCard = ({ icon, type, models, passengers, price, highlight }) => (
  <Box sx={{
    p: 3, borderRadius: '20px', textAlign: 'center', position: 'relative',
    background: highlight ? PURPLE_BG : '#fff',
    border: highlight ? `2px solid ${PURPLE}` : '1.5px solid #eee',
    boxShadow: highlight ? `0 12px 36px rgba(91,45,142,0.18)` : '0 4px 14px rgba(0,0,0,0.05)',
    transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-6px)', boxShadow: `0 18px 44px rgba(91,45,142,0.15)` },
  }}>
    {highlight && <Box sx={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', bgcolor: PURPLE, color: '#fff', borderRadius: '20px', px: 2.5, py: 0.5, fontWeight: 800, fontSize: '0.68rem', fontFamily: "'Poppins',sans-serif", whiteSpace: 'nowrap', boxShadow: '0 4px 12px rgba(91,45,142,0.35)' }}>BEST SELLER</Box>}
    <Box sx={{ mb: 1.5 }}>{icon}</Box>
    <Typography sx={{ color: TEXT_DARK, fontWeight: 800, fontFamily: "'Poppins',sans-serif", fontSize: '1.15rem', mb: 0.3 }}>{type}</Typography>
    <Typography sx={{ color: TEXT_MID, fontFamily: "'Poppins',sans-serif", fontSize: '0.75rem', mb: 1.5 }}>{models}</Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 2 }}>
      <GroupsIcon sx={{ fontSize: 15, color: TEXT_LIGHT }} />
      <Typography sx={{ color: TEXT_MID, fontFamily: "'Poppins',sans-serif", fontSize: '0.78rem' }}>{passengers}</Typography>
    </Box>
    <Divider sx={{ borderColor: '#f0f0f0', mb: 2 }} />
    <Typography sx={{ color: TEXT_LIGHT, fontFamily: "'Poppins',sans-serif", fontSize: '0.7rem', mb: 0.5 }}>Round Trip Starting From</Typography>
    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.3, mb: 0.3 }}>
      <CurrencyRupeeIcon sx={{ color: PURPLE, fontSize: 22, mt: 0.5 }} />
      <Typography sx={{ color: PURPLE, fontWeight: 900, fontFamily: "'Poppins',sans-serif", fontSize: '2.3rem', lineHeight: 1 }}>{price}</Typography>
    </Box>
    <Typography sx={{ color: TEXT_LIGHT, fontFamily: "'Poppins',sans-serif", fontSize: '0.68rem', mb: 2.5 }}>inclusive of all taxes</Typography>
    <Button component="a" href="tel:+919964848414" fullWidth sx={{ py: 1.1, borderRadius: '12px', textTransform: 'none', fontWeight: 700, fontFamily: "'Poppins',sans-serif", fontSize: '0.85rem', bgcolor: highlight ? PURPLE : PURPLE_BG, color: highlight ? '#fff' : PURPLE, '&:hover': { bgcolor: highlight ? PURPLE_DK : '#e4ddf0' }, transition: 'all 0.2s', boxShadow: highlight ? '0 4px 14px rgba(91,45,142,0.3)' : 'none' }}>
      Call to Book
    </Button>
  </Box>
);

const ReviewCard = ({ name, loc, text, stars }) => (
  <Box sx={{ p: 3, borderRadius: '20px', background: '#fff', border: '1.5px solid #eee', boxShadow: '0 4px 16px rgba(0,0,0,0.05)', height: '100%', transition: 'all 0.3s', '&:hover': { boxShadow: '0 10px 30px rgba(91,45,142,0.1)', transform: 'translateY(-3px)', border: `1.5px solid rgba(91,45,142,0.2)` } }}>
    <Box sx={{ display: 'flex', mb: 1.5 }}>{[...Array(stars)].map((_, i) => <StarIcon key={i} sx={{ color: '#F59E0B', fontSize: 18 }} />)}</Box>
    <FormatQuoteIcon sx={{ color: PURPLE, fontSize: 28, mb: 0.5, opacity: 0.35 }} />
    <Typography sx={{ color: TEXT_MID, fontFamily: "'Poppins',sans-serif", fontSize: '0.85rem', lineHeight: 1.8, mb: 2, fontStyle: 'italic' }}>"{text}"</Typography>
    <Divider sx={{ borderColor: '#f3f3f3', mb: 1.5 }} />
    <Typography sx={{ color: TEXT_DARK, fontWeight: 700, fontFamily: "'Poppins',sans-serif", fontSize: '0.88rem' }}>{name}</Typography>
    <Typography sx={{ color: TEXT_LIGHT, fontFamily: "'Poppins',sans-serif", fontSize: '0.72rem', mt: 0.2 }}>{loc}</Typography>
  </Box>
);

const TirumalaLandingPage = () => (
  <Box sx={{ bgcolor: '#ffffff', fontFamily: "'Poppins',sans-serif" }}>
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800;900&display=swap');
      * { box-sizing: border-box; }
    `}</style>

    {/* ── NAVBAR ── */}
    <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, backgroundColor: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(20px)', borderBottom: '1px solid #f0eef8', height: { xs: '70px', md: '80px' }, display: 'flex', alignItems: 'center', boxShadow: '0 2px 16px rgba(91,45,142,0.06)' }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box component="a" href="/" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', height: { xs: 56, md: 70 }, position: 'relative', width: { xs: 140, md: 180 } }}>
            <Box component="img" src="/vihaan-tours-logo.png" alt="Vihaan Tours" sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', height: { xs: 90, md: 130 }, width: 'auto', maxWidth: 'none' }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-end', md: 'center' }, gap: { xs: 0.5, md: 1.5 } }}>
            <Box component="a" href="mailto:vihaantoursandtravels2025@gmail.com"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: TEXT_MID, px: { xs: 0, md: 2 }, py: { xs: 0, md: 0.8 }, bgcolor: { xs: 'transparent', md: PURPLE_BG }, borderRadius: { xs: 0, md: '25px' }, '&:hover': { color: PURPLE }, transition: 'all 0.2s' }}>
              <EmailIcon sx={{ fontSize: { xs: '0.9rem', md: '1rem' }, color: PURPLE }} />
              <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.6rem', sm: '0.72rem', md: '0.82rem' }, fontFamily: "'Poppins',sans-serif", color: { xs: TEXT_MID, md: TEXT_DARK } }}>
                vihaantoursandtravels2025@gmail.com
              </Typography>
            </Box>
            <Box component="a" href="tel:+919964848414"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', bgcolor: PURPLE, color: '#fff', px: { xs: 1.5, md: 2.2 }, py: { xs: 0.7, md: 0.9 }, borderRadius: '50px', fontWeight: 700, fontSize: { xs: '0.72rem', md: '0.85rem' }, fontFamily: "'Poppins',sans-serif", boxShadow: '0 4px 14px rgba(91,45,142,0.3)', '&:hover': { bgcolor: PURPLE_DK }, transition: 'all 0.2s' }}>
              <PhoneIcon sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }} /> +91 99648 48414
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>

    {/* ── HERO ── */}
    <Box sx={{ pt: { xs: '70px', md: '80px' } }}>
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'stretch', md: 'center' }, gap: { xs: 4, md: 6 }, minHeight: { md: 'calc(100vh - 80px)' } }}>

          {/* LEFT col */}
          <Box sx={{ flex: 1.2, display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', md: 'flex-start' } }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8, bgcolor: PURPLE_BG, border: `1px solid rgba(91,45,142,0.2)`, borderRadius: '50px', px: 2, py: 0.6, mb: 2.5 }}>
              <LocationOnIcon sx={{ fontSize: 14, color: PURPLE }} />
              <Typography sx={{ color: PURPLE, fontSize: '0.78rem', fontWeight: 600, fontFamily: "'Poppins',sans-serif" }}>Bangalore → Tirumala · ~280 km</Typography>
            </Box>

            <Typography component="h1" sx={{ fontWeight: 800, color: TEXT_DARK, fontFamily: "'Poppins',sans-serif", fontSize: { xs: '1.9rem', sm: '2.4rem', md: '2.8rem' }, lineHeight: 1.1, mb: 1.5, textAlign: { xs: 'center', md: 'left' } }}>
              Bangalore to{' '}
              <Box component="span" sx={{ color: PURPLE, textDecoration: 'underline', textDecorationColor: PURPLE, textUnderlineOffset: '6px', textDecorationThickness: '3px' }}>Tirumala</Box>{' '}
              Family Package
            </Typography>
            <Typography sx={{ color: PURPLE, fontWeight: 600, fontFamily: "'Poppins',sans-serif", fontSize: { xs: '0.9rem', md: '1.05rem' }, mb: 3, fontStyle: 'italic', textAlign: { xs: 'center', md: 'left' } }}>
              — Exclusive Temples Tour with Cab
            </Typography>

            {/* Temple image */}
            <Box sx={{ width: '100%', mb: 3, borderRadius: '20px', overflow: 'hidden', boxShadow: '0 16px 48px rgba(91,45,142,0.14)', border: `2px solid rgba(91,45,142,0.1)`, position: 'relative' }}>
              <Box component="img" src={HERO_IMG} alt="Tirumala Temple"
                onError={e => { e.target.onerror = null; e.target.src = 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/b7/Group_shot_at_Tirupati.jpg/1280px-Group_shot_at_Tirupati.jpg'; }}
                sx={{ width: '100%', height: { xs: 200, sm: 260, md: 300 }, objectFit: 'cover', display: 'block' }} />
              <Box sx={{ position: 'absolute', bottom: 14, left: 14, bgcolor: 'rgba(255,255,255,0.96)', borderRadius: '12px', px: 1.8, py: 0.9, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ bgcolor: PURPLE, color: '#fff', px: 1.2, py: 0.4, borderRadius: '8px', fontWeight: 800, fontSize: '0.68rem', fontFamily: "'Poppins',sans-serif", whiteSpace: 'nowrap' }}>1 Night &amp; 1 Day</Box>
                <Typography sx={{ color: PURPLE, fontWeight: 900, fontSize: '0.88rem', fontFamily: "'Poppins',sans-serif", whiteSpace: 'nowrap' }}>₹2,499/- per person</Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2, mb: 2.5, justifyContent: { xs: 'center', md: 'flex-start' } }}>
              {['SRI VENKATESWARA SWAMY TEMPLE', 'SRI PADMAVATHI AMMAVARI TEMPLE'].map((t, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.6, bgcolor: PURPLE_BG, border: `1px solid rgba(91,45,142,0.18)`, px: 1.8, py: 0.6, borderRadius: '50px' }}>
                  <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: PURPLE }} />
                  <Typography sx={{ color: PURPLE, fontWeight: 700, fontSize: '0.72rem', fontFamily: "'Poppins',sans-serif" }}>{t}</Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ bgcolor: PURPLE_BG, border: `1px solid rgba(91,45,142,0.15)`, p: 2, borderRadius: '14px', mb: 2.5, width: '100%' }}>
              <Typography sx={{ color: TEXT_DARK, fontSize: '0.84rem', lineHeight: 1.7, fontFamily: "'Poppins',sans-serif" }}>
                <Box component="span" sx={{ fontWeight: 700, color: PURPLE, mr: 1 }}>INCLUDES:</Box>
                AC Vehicle, Permit, Toll Charges, Driver Charges, Accommodation Guidance, Darshan Assistance
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-start' } }}>
              <Button component="a" href="tel:+919964848414" startIcon={<PhoneIcon />}
                sx={{ bgcolor: PURPLE, color: '#fff', fontWeight: 700, fontFamily: "'Poppins',sans-serif", borderRadius: '50px', px: 3, py: 1.2, fontSize: '0.9rem', textTransform: 'none', boxShadow: '0 4px 16px rgba(91,45,142,0.3)', '&:hover': { bgcolor: PURPLE_DK }, transition: 'all 0.2s' }}>
                Call Now
              </Button>
              <Button component="a" href="https://wa.me/919964848414?text=Hi, I want to book Tirumala cab" target="_blank" startIcon={<WhatsAppIcon />}
                sx={{ bgcolor: '#25D366', color: '#fff', fontWeight: 700, fontFamily: "'Poppins',sans-serif", borderRadius: '50px', px: 3, py: 1.2, fontSize: '0.9rem', textTransform: 'none', '&:hover': { bgcolor: '#22c55e' }, transition: 'all 0.2s' }}>
                WhatsApp
              </Button>
            </Box>
          </Box>

          {/* RIGHT col — booking form */}
          <Box sx={{ flex: 1, minWidth: 0, maxWidth: { xs: '100%', md: 420 }, width: '100%', alignSelf: 'center' }}>
            <BookingCard />
          </Box>
        </Box>
      </Container>
    </Box>

    {/* ── ROUTE STRIP ── */}
    <Box sx={{ bgcolor: PURPLE_BG, borderTop: '1px solid rgba(91,45,142,0.1)', borderBottom: '1px solid rgba(91,45,142,0.1)', py: 2.5 }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: { xs: 2, md: 5 } }}>
          {[
            { icon: <LocationOnIcon sx={{ fontSize: 16, color: PURPLE }} />, t: 'Pickup: Any location in Bangalore' },
            { icon: <RouteIcon sx={{ fontSize: 16, color: PURPLE }} />, t: '~280 km via NH 44 / Chittoor Highway' },
            { icon: <AccessTimeIcon sx={{ fontSize: 16, color: PURPLE }} />, t: '~5–6 hours comfortable drive' },
            { icon: <LocationOnIcon sx={{ fontSize: 16, color: '#e11d48' }} />, t: 'Drop: Tirumala Temple Main Gate' },
          ].map((x, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
              {x.icon}
              <Typography sx={{ color: TEXT_MID, fontSize: { xs: '0.76rem', md: '0.86rem' }, fontFamily: "'Poppins',sans-serif" }}>{x.t}</Typography>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>

    {/* ── ABOUT TIRUMALA ── */}
    <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: '#fff' }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 4, md: 8 }, alignItems: 'center' }}>
          <Box sx={{ flex: 1, borderRadius: '24px', overflow: 'hidden', boxShadow: '0 16px 48px rgba(91,45,142,0.12)', border: `2px solid rgba(91,45,142,0.08)` }}>
            <Box component="img"
              src={TEMPLE_IMG}
              alt="Sri Venkateswara Swamy Tirumala"
              sx={{ width: '100%', height: { xs: 240, md: 400 }, objectFit: 'cover', display: 'block' }} />
          </Box>
          <Box sx={{ flex: 1.2 }}>
            <Box sx={{ display: 'inline-flex', bgcolor: PURPLE_BG, borderRadius: '6px', px: 1.5, py: 0.4, mb: 1.5 }}>
              <Typography sx={{ color: PURPLE, fontWeight: 700, fontSize: '0.72rem', letterSpacing: '1.5px', fontFamily: "'Poppins',sans-serif" }}>ABOUT THE DESTINATION</Typography>
            </Box>
            <Typography sx={{ color: TEXT_DARK, fontWeight: 800, fontFamily: "'Poppins',sans-serif", fontSize: { xs: '1.6rem', md: '2.2rem' }, lineHeight: 1.2, mb: 2 }}>
              The Sacred Hills of <Box component="span" sx={{ color: PURPLE }}>Lord Venkateswara</Box>
            </Typography>
            <Typography sx={{ color: TEXT_MID, fontFamily: "'Poppins',sans-serif", fontSize: '0.9rem', lineHeight: 1.85, mb: 2.5 }}>
              Tirumala, nestled in the Seshachalam hills of Andhra Pradesh, is home to the Sri Venkateswara Swamy Temple — one of the richest and most visited religious shrines in the world, drawing over 50,000 devotees every day.
            </Typography>
            {["Sri Venkateswara Swamy Temple — world's most visited pilgrimage site", 'Sri Padmavathi Ammavari Temple in Tiruchanur (Tirupati)', 'Scenic Tirumala Ghat roads through lush Nallamala hills', "Srivari Laddu prasadam — world's most famous temple sweet", 'Akasa Ganga, Papavinasanam & other sacred spots nearby'].map((p, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2, mb: 1.1 }}>
                <CheckCircleIcon sx={{ color: PURPLE, fontSize: 16, mt: 0.3, flexShrink: 0 }} />
                <Typography sx={{ color: TEXT_MID, fontFamily: "'Poppins',sans-serif", fontSize: '0.86rem', lineHeight: 1.65 }}>{p}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>

    {/* ── PRICING ── */}
    <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: PURPLE_BG }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
          <Box sx={{ display: 'inline-flex', bgcolor: '#fff', border: `1px solid rgba(91,45,142,0.2)`, borderRadius: '6px', px: 1.5, py: 0.4, mb: 1.5 }}>
            <Typography sx={{ color: PURPLE, fontWeight: 700, fontSize: '0.72rem', letterSpacing: '1.5px', fontFamily: "'Poppins',sans-serif" }}>TRANSPARENT PRICING</Typography>
          </Box>
          <Typography sx={{ color: TEXT_DARK, fontWeight: 900, fontFamily: "'Poppins',sans-serif", fontSize: { xs: '1.8rem', md: '2.4rem' } }}>Choose Your Vehicle &amp; Fare</Typography>
          <Typography sx={{ color: TEXT_MID, fontFamily: "'Poppins',sans-serif", fontSize: '0.9rem', mt: 1 }}>All fares round-trip · Toll &amp; driver batta included · No hidden charges</Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
          <PricingCard icon={<DirectionsCarIcon sx={{ fontSize: 44, color: PURPLE }} />} type="Sedan" models="Swift Dzire / Toyota Etios" passengers="1–4 Passengers" price="6,499" highlight={false} />
          <PricingCard icon={<AirportShuttleIcon sx={{ fontSize: 44, color: PURPLE }} />} type="SUV / MUV" models="Innova Crysta / Ertiga" passengers="1–7 Passengers" price="9,499" highlight={true} />
          <PricingCard icon={<DirectionsBusIcon sx={{ fontSize: 44, color: PURPLE }} />} type="Tempo Traveller" models="12 / 14 Seater Mini Bus" passengers="8–14 Passengers" price="17,499" highlight={false} />
        </Box>
      </Container>
    </Box>

    {/* ── WHY CHOOSE US ── */}
    <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: '#fff' }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
          <Box sx={{ display: 'inline-flex', bgcolor: PURPLE_BG, borderRadius: '6px', px: 1.5, py: 0.4, mb: 1.5 }}>
            <Typography sx={{ color: PURPLE, fontWeight: 700, fontSize: '0.72rem', letterSpacing: '1.5px', fontFamily: "'Poppins',sans-serif" }}>WHY PILGRIMS TRUST US</Typography>
          </Box>
          <Typography sx={{ color: TEXT_DARK, fontWeight: 900, fontFamily: "'Poppins',sans-serif", fontSize: { xs: '1.8rem', md: '2.4rem' } }}>Trusted Tirumala Cab Service</Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(6,1fr)' }, gap: 2 }}>
          {[
            { icon: <DirectionsCarIcon sx={{ fontSize: 34, color: PURPLE }} />, t: 'AC Cabs', s: 'Fully AC & sanitized before every trip' },
            { icon: <PersonIcon sx={{ fontSize: 34, color: PURPLE }} />, t: 'Ghat Expert', s: 'Trained on Tirumala ghat roads' },
            { icon: <AccountBalanceWalletIcon sx={{ fontSize: 34, color: PURPLE }} />, t: 'Best Price', s: 'No hidden charges, lowest fare guarantee' },
            { icon: <SecurityIcon sx={{ fontSize: 34, color: PURPLE }} />, t: 'GPS Tracked', s: '24/7 tracking for your safety' },
            { icon: <SupportAgentIcon sx={{ fontSize: 34, color: PURPLE }} />, t: '24/7 Support', s: 'Always on call & WhatsApp' },
            { icon: <SelfImprovementIcon sx={{ fontSize: 34, color: PURPLE }} />, t: 'Puja-Friendly', s: 'Flexible for darshan queue timings' },
          ].map((f, i) => (
            <Box key={i} sx={{ textAlign: 'center', p: 2.5, borderRadius: '18px', bgcolor: '#fff', border: '1.5px solid #eee', transition: 'all 0.3s', '&:hover': { border: `1.5px solid rgba(91,45,142,0.3)`, bgcolor: PURPLE_BG, transform: 'translateY(-4px)', boxShadow: '0 10px 28px rgba(91,45,142,0.1)' } }}>
              <Box sx={{ mb: 1.2 }}>{f.icon}</Box>
              <Typography sx={{ color: TEXT_DARK, fontWeight: 700, fontFamily: "'Poppins',sans-serif", fontSize: '0.85rem', mb: 0.4 }}>{f.t}</Typography>
              <Typography sx={{ color: TEXT_MID, fontFamily: "'Poppins',sans-serif", fontSize: '0.7rem', lineHeight: 1.5 }}>{f.s}</Typography>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>

    {/* ── REVIEWS ── */}
    <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: PURPLE_BG }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
          <Box sx={{ display: 'inline-flex', bgcolor: '#fff', border: `1px solid rgba(91,45,142,0.2)`, borderRadius: '6px', px: 1.5, py: 0.4, mb: 1.5 }}>
            <Typography sx={{ color: PURPLE, fontWeight: 700, fontSize: '0.72rem', letterSpacing: '1.5px', fontFamily: "'Poppins',sans-serif" }}>HAPPY CUSTOMERS</Typography>
          </Box>
          <Typography sx={{ color: TEXT_DARK, fontWeight: 900, fontFamily: "'Poppins',sans-serif", fontSize: { xs: '1.8rem', md: '2.4rem' } }}>What Our Pilgrims Say</Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
          <ReviewCard name="Suresh Kumar" loc="Koramangala, Bangalore" stars={5} text="Perfect Tirumala cab service! Driver knew all the ghat shortcuts. Spotless Innova Crysta, AC perfect through the hills. Will only use Vihaan for future Tirupati trips!" />
          <ReviewCard name="Anita Reddy" loc="Whitefield, Bangalore" stars={5} text="Family of 5 travelled with Vihaan Tours. Driver was courteous and helped us with Sudarshana token. Pickup on time at 4 AM! Best Tirumala cab experience by far." />
          <ReviewCard name="Ravi Shankar" loc="Jayanagar, Bangalore" stars={5} text="Booked online at midnight, confirmation in 5 minutes. Price exactly as quoted — no extras. Comfortable, clean, punctual. This is how a pilgrimage cab service should be!" />
        </Box>
      </Container>
    </Box>

    {/* ── CTA ── */}
    <Box sx={{ py: { xs: 7, md: 10 }, bgcolor: PURPLE, textAlign: 'center' }}>
      <Container maxWidth="md">
        <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 700, fontFamily: "'Poppins',sans-serif", fontSize: '0.75rem', letterSpacing: '2px', mb: 1.5 }}>START YOUR SPIRITUAL JOURNEY TODAY</Typography>
        <Typography sx={{ color: '#fff', fontWeight: 900, fontFamily: "'Poppins',sans-serif", fontSize: { xs: '1.8rem', md: '2.6rem' }, mb: 1.5, lineHeight: 1.2 }}>
          Book Bangalore to Tirumala Cab
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontFamily: "'Poppins',sans-serif", fontSize: '0.95rem', mb: 4 }}>
          Instant confirmation · Best price guarantee · No advance payment
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button component="a" href="tel:+919964848414" startIcon={<PhoneIcon />}
            sx={{ bgcolor: '#fff', color: PURPLE, fontWeight: 800, fontFamily: "'Poppins',sans-serif", borderRadius: '50px', px: 4, py: 1.5, fontSize: '1rem', textTransform: 'none', boxShadow: '0 6px 20px rgba(0,0,0,0.2)', '&:hover': { bgcolor: PURPLE_BG }, transition: 'all 0.2s' }}>
            Call Now
          </Button>
          <Button component="a" href="https://wa.me/919964848414?text=Hi, I want to book Tirumala cab" target="_blank" startIcon={<WhatsAppIcon />}
            sx={{ bgcolor: '#25D366', color: '#fff', fontWeight: 700, fontFamily: "'Poppins',sans-serif", borderRadius: '50px', px: 4, py: 1.5, fontSize: '1rem', textTransform: 'none', '&:hover': { bgcolor: '#22c55e' }, transition: 'all 0.2s' }}>
            WhatsApp Us
          </Button>
        </Box>
      </Container>
    </Box>

    {/* ── FOOTER ── */}
    <Box sx={{ py: 3, textAlign: 'center', bgcolor: PURPLE_DK }}>
      <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontFamily: "'Poppins',sans-serif", fontSize: '0.75rem' }}>
        © 2025 Vihaan Tours &amp; Travels · Bangalore to Tirumala Cab ·{' '}
        <Box component="a" href="/" sx={{ color: 'rgba(255,255,255,0.75)', textDecoration: 'none', '&:hover': { color: '#fff' } }}>Home</Box>{' '}·{' '}
        <Box component="a" href="tel:+919964848414" sx={{ color: 'rgba(255,255,255,0.75)', textDecoration: 'none', '&:hover': { color: '#fff' } }}>+91 99648 48414</Box>
      </Typography>
    </Box>
  </Box>
);

export default TirumalaLandingPage;
